import { useEffect, useRef, useState } from "react";
import api, { resolveImageUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Sparkles, Loader2, Plus, AlertCircle, ExternalLink } from "lucide-react";

const VARIANTS = ["Standard", "Chase", "BLKLTD"];
const SCALES = ["1:64", "1:43", "1:32", "1:24", "1:18"];

export default function SuggestReleaseDialog({ trigger, open: openProp, onOpenChange, prefill = null, onSubmitted }) {
  const [openState, setOpenState] = useState(false);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : openState;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v);
    else setOpenState(v);
  };
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    brand: "",
    release_name: "",
    vehicle: "",
    variant_type: "Standard",
    colorway: "",
    series: "",
    scale: "1:64",
    estimated_market_value: "",
    upc: "",
    notes: "",
    ...(prefill || {}),
  });

  // Re-prefill whenever the dialog opens with new prefill data
  useEffect(() => {
    if (open && prefill) {
      setForm((f) => ({ ...f, ...prefill }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Duplicate detection — debounced GPT-backed check against existing catalog.
  const [duplicateMatch, setDuplicateMatch] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  const [acknowledgedDup, setAcknowledgedDup] = useState(false);
  const dupTimerRef = useRef(null);
  const dupReqIdRef = useRef(0);

  // Trigger when name/vehicle/brand have enough signal. Reset acknowledge whenever the user
  // edits the fields after a match was shown (so they can't sneak past via stale state).
  useEffect(() => {
    if (!open) return;
    setAcknowledgedDup(false);
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    const name = form.release_name.trim();
    const brand = form.brand.trim();
    if (name.length < 4) {
      setDuplicateMatch(null);
      setDupChecking(false);
      return;
    }
    dupTimerRef.current = setTimeout(async () => {
      const reqId = ++dupReqIdRef.current;
      setDupChecking(true);
      try {
        const { data } = await api.post("/releases/check-duplicate", {
          brand,
          release_name: name,
          vehicle: form.vehicle.trim(),
          colorway: form.colorway.trim(),
          variant_type: form.variant_type,
        });
        if (reqId !== dupReqIdRef.current) return; // stale response
        setDuplicateMatch(data?.match || null);
      } catch {
        if (reqId === dupReqIdRef.current) setDuplicateMatch(null);
      } finally {
        if (reqId === dupReqIdRef.current) setDupChecking(false);
      }
    }, 700);
    return () => {
      if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    };
  }, [form.brand, form.release_name, form.vehicle, form.colorway, form.variant_type, open]);

  const reset = () => {
    setForm({
      brand: "", release_name: "", vehicle: "", variant_type: "Standard",
      colorway: "", series: "", scale: "1:64", estimated_market_value: "",
      upc: "", notes: "",
    });
    setDuplicateMatch(null);
    setAcknowledgedDup(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.brand.trim() || !form.release_name.trim()) {
      toast.error("Brand and release name are required");
      return;
    }
    if (duplicateMatch && !acknowledgedDup) {
      toast.error("Possible duplicate detected — review or click 'Submit anyway'");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        estimated_market_value: form.estimated_market_value
          ? Number(form.estimated_market_value)
          : null,
      };
      const { data } = await api.post("/releases/suggest", payload);
      toast.success("Suggestion submitted — an admin will review it soon");
      reset();
      setOpen(false);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button
              data-testid="suggest-release-btn"
              variant="outline"
              size="sm"
              className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-purple-500/40"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5 text-purple-400" /> Suggest a Release
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent
        data-testid="suggest-release-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-zinc-100 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" /> Suggest a Release
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Know a release that isn&apos;t in the catalog yet? Submit it for review. If approved, it joins the
            public catalog and your existing custom entries auto-link.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Brand *</Label>
              <Input
                data-testid="suggest-brand"
                value={form.brand}
                onChange={(e) => update("brand", e.target.value)}
                placeholder="Hot Wheels, Mini GT, Inno64…"
                className="bg-zinc-900 border-zinc-800 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Scale</Label>
              <select
                data-testid="suggest-scale"
                value={form.scale}
                onChange={(e) => update("scale", e.target.value)}
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100"
              >
                {SCALES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Release Name *</Label>
            <Input
              data-testid="suggest-release-name"
              value={form.release_name}
              onChange={(e) => update("release_name", e.target.value)}
              placeholder="e.g. Skyline GT-R R34 V-Spec II"
              className="bg-zinc-900 border-zinc-800 mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Vehicle</Label>
              <Input
                data-testid="suggest-vehicle"
                value={form.vehicle}
                onChange={(e) => update("vehicle", e.target.value)}
                placeholder="Nissan Skyline R34"
                className="bg-zinc-900 border-zinc-800 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Variant</Label>
              <select
                data-testid="suggest-variant"
                value={form.variant_type}
                onChange={(e) => update("variant_type", e.target.value)}
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100"
              >
                {VARIANTS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Series</Label>
              <Input
                value={form.series}
                onChange={(e) => update("series", e.target.value)}
                placeholder="Car Culture / RLC / etc."
                className="bg-zinc-900 border-zinc-800 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Colorway</Label>
              <Input
                value={form.colorway}
                onChange={(e) => update("colorway", e.target.value)}
                placeholder="Bayside Blue"
                className="bg-zinc-900 border-zinc-800 mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Market Value $</Label>
              <Input
                data-testid="suggest-market-value"
                type="number"
                min="0"
                step="0.5"
                value={form.estimated_market_value}
                onChange={(e) => update("estimated_market_value", e.target.value)}
                placeholder="Optional"
                className="bg-zinc-900 border-zinc-800 mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">UPC (optional)</Label>
              <Input
                data-testid="suggest-upc"
                value={form.upc}
                onChange={(e) => update("upc", e.target.value.replace(/\D/g, "").slice(0, 13))}
                placeholder="12 or 13 digits"
                className="bg-zinc-900 border-zinc-800 mt-1 font-mono"
                maxLength={13}
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Anything helpful for the reviewer (release date, source link, rarity, etc.)"
              className="bg-zinc-900 border-zinc-800 mt-1 text-sm"
              rows={3}
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            {dupChecking && !duplicateMatch && (
              <span data-testid="dup-checking" className="text-[10px] text-zinc-500 flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Checking catalog...
              </span>
            )}
            <Button
              type="submit"
              data-testid="suggest-submit"
              disabled={submitting || (duplicateMatch && !acknowledgedDup)}
              className={
                duplicateMatch && acknowledgedDup
                  ? "bg-amber-600 hover:bg-amber-500 text-white"
                  : "bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-50"
              }
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
              {duplicateMatch && acknowledgedDup ? "Submit anyway" : "Submit for review"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-zinc-500 hover:text-zinc-100"
            >
              Cancel
            </Button>
          </div>
        </form>

        {/* Possible-duplicate warning panel */}
        {duplicateMatch && (
          <div
            data-testid="duplicate-warning"
            className="mt-4 rounded-xl bg-gradient-to-br from-amber-950/30 to-zinc-900/60 border border-amber-500/30 p-3"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-200">
                  This may already be in the catalog
                  {duplicateMatch.confidence === "high" && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-400 bg-amber-500/15 border border-amber-500/30 rounded-full px-1.5 py-0.5">High confidence</span>}
                  {duplicateMatch.confidence === "medium" && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-full px-1.5 py-0.5">Likely</span>}
                </p>
                {duplicateMatch.reason && (
                  <p className="text-[11px] text-zinc-400 mt-0.5 italic">&ldquo;{duplicateMatch.reason}&rdquo;</p>
                )}
                <Link
                  to={`/catalog/${duplicateMatch.release_id}`}
                  target="_blank"
                  data-testid="duplicate-existing-link"
                  className="mt-2 flex items-center gap-3 rounded-lg bg-zinc-950/60 border border-amber-500/15 hover:border-amber-500/40 p-2 transition-colors"
                >
                  <div className="w-12 h-12 rounded bg-zinc-900 border border-zinc-800 overflow-hidden flex-shrink-0">
                    {duplicateMatch.model_image ? (
                      <img src={resolveImageUrl(duplicateMatch.model_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[8px] text-zinc-700 font-mono">{duplicateMatch.khmg_number || "—"}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-100 truncate">{duplicateMatch.release_name}</p>
                    <p className="text-[10px] text-zinc-500 font-mono truncate">
                      {duplicateMatch.khmg_number || "—"} · {duplicateMatch.brand || "—"} · {duplicateMatch.vehicle || "—"}
                    </p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                </Link>
                <div className="flex items-center gap-2 mt-2">
                  {!acknowledgedDup ? (
                    <Button
                      type="button"
                      data-testid="dup-confirm-different"
                      onClick={() => setAcknowledgedDup(true)}
                      size="sm"
                      variant="outline"
                      className="bg-zinc-950 border-amber-500/30 text-amber-300 hover:bg-amber-500/10 hover:border-amber-500/50 text-xs"
                    >
                      It&apos;s a different release
                    </Button>
                  ) : (
                    <p className="text-[10px] text-zinc-500 italic">Acknowledged — you can now submit anyway.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
